import ReCAPTCHA from 'react-google-recaptcha';
import axios from 'axios';
import { useEffect, useRef, useState } from 'react';
import { PAGE_META, usePageMeta } from '../lib/pageMeta';

const API = process.env.REACT_APP_API_URL || 'http://localhost:5000';

const QUALIFICATION_OPTIONS = [
  '10th / SSC',
  '12th / Intermediate',
  'Diploma',
  "Bachelor's Degree (B.Tech / B.E / B.Sc / BCA / B.Com / BA)",
  "Master's Degree (M.Tech / M.Sc / MCA / MBA)",
  'PhD',
  'Other',
];

const SOURCE_OPTIONS = [
  { value: 'phone_enquiry', label: 'Phone Enquiry' },
  { value: 'website',       label: 'Website / Google Search' },
  { value: 'social',        label: 'Social Media (Facebook / Instagram)' },
  { value: 'reference',     label: 'Friend / Reference' },
  { value: 'walkin',        label: 'Walk-in' },
  { value: 'other',         label: 'Other' },
];

const YEAR_OPTIONS = Array.from({ length: 15 }, (_, i) => String(new Date().getFullYear() - i));

/* ── useWindowWidth hook ─────────────────────────────────────────────────── */
function useWindowWidth() {
  const [width, setWidth] = useState(() => window.innerWidth);
  useEffect(() => {
    const h = () => setWidth(window.innerWidth);
    window.addEventListener('resize', h);
    return () => window.removeEventListener('resize', h);
  }, []);
  return width;
}

/* ── Shared input base style ─────────────────────────────────────────────── */
const baseInp = {
  width: '100%',
  padding: '10px 13px',
  border: '1.5px solid #d1d5db',
  borderRadius: 7,
  fontSize: 14,
  color: '#111827',
  background: '#fff',
  outline: 'none',
  boxSizing: 'border-box',
  fontFamily: 'inherit',
  transition: 'border-color 0.2s, box-shadow 0.2s',
};

function Field({ label, required, children, isMobile, half }) {
  const flex = half && !isMobile ? '1 1 calc(50% - 10px)' : '1 1 100%';
  return (
    <div style={{ flex, minWidth: 0 }}>
      <label style={{ display: 'block', marginBottom: 5, fontSize: 13, fontWeight: 600, color: '#374151' }}>
        {label}{required && <span style={{ color: '#e53e3e' }}> *</span>}
      </label>
      {children}
    </div>
  );
}

function Row({ children, isMobile }) {
  return (
    <div style={{ display: 'flex', flexWrap: 'wrap', gap: isMobile ? 12 : 20, marginBottom: isMobile ? 12 : 16 }}>
      {children}
    </div>
  );
}

/* ══════════════════════════════════════════════════════════════════════════ */
export default function EnquiryForm() {
  const winW     = useWindowWidth();
  const isMobile = winW < 640;

  usePageMeta(PAGE_META.enquiry);

  const [courses,   setCourses]   = useState([]);
  const [batches,   setBatches]   = useState([]);
  const [loading,   setLoading]   = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [error,     setError]     = useState('');
  const [focused,   setFocused]   = useState('');
  const fetchingRef   = useRef(false);
  const recaptchaRef  = useRef(null);

  /* ── reCAPTCHA token ────────────────────────────────────────────────────── */
  const [recaptchaToken, setRecaptchaToken] = useState('');

  const [form, setForm] = useState({
    name: '', phone: '', email: '',
    qualification: '', yearOfPassedOut: '',
    courseId: '', batchId: '', batchTimings: '',
    preferredMode: '', source: '', referencePersonName: '',
  });

  useEffect(() => {
    axios.get(`${API}/public/courses`)
      .then(r => setCourses(r.data.data || []))
      .catch(() => setCourses([]));
  }, []);

  useEffect(() => {
    if (!form.courseId) {
      setBatches([]);
      setForm(f => ({ ...f, batchId: '', batchTimings: '' }));
      return;
    }
    if (fetchingRef.current) return;
    fetchingRef.current = true;
    axios.get(`${API}/public/courses/${form.courseId}/batches`)
      .then(r => setBatches(r.data.data || []))
      .catch(() => setBatches([]))
      .finally(() => { fetchingRef.current = false; });
    setForm(f => ({ ...f, batchId: '', batchTimings: '' }));
  }, [form.courseId]);

  useEffect(() => {
    if (!form.batchId) { setForm(f => ({ ...f, batchTimings: '' })); return; }
    const batch = batches.find(b => b._id === form.batchId);
    if (batch) setForm(f => ({ ...f, batchTimings: batch.timingStr }));
  }, [form.batchId, batches]);

  const set  = key => e  => setForm(f => ({ ...f, [key]: e.target.value }));
  const inp  = key => ({
    ...baseInp,
    borderColor: focused === key ? '#e05b1a' : '#d1d5db',
    boxShadow:   focused === key ? '0 0 0 3px rgba(224,91,26,0.15)' : 'none',
    fontSize: isMobile ? 15 : 14,
    padding: isMobile ? '11px 13px' : '10px 13px',
  });
  const fb   = key => ({ onFocus: () => setFocused(key), onBlur: () => setFocused('') });

  const handleSubmit = async e => {
    e.preventDefault();
    setError('');

    if (!form.name.trim() || !form.phone.trim()) {
      setError('Name and phone number are required.');
      return;
    }

    if (!recaptchaToken) {
      setError('Please complete the reCAPTCHA verification.');
      return;
    }

    setLoading(true);
    try {
      await axios.post(`${API}/public/enquiry`, {
        ...form,
        _hp: '',           // honeypot — always blank for real users
        recaptchaToken,
      });
      setSubmitted(true);
    } catch (err) {
      setError(err?.response?.data?.message || 'Submission failed. Please try again.');
      // Reset reCAPTCHA after failure so user can try again
      setRecaptchaToken('');
      recaptchaRef.current?.reset();
    } finally {
      setLoading(false);
    }
  };

  const resetForm = () => {
    setSubmitted(false);
    setForm({ name:'',phone:'',email:'',qualification:'',yearOfPassedOut:'',courseId:'',batchId:'',batchTimings:'',preferredMode:'',source:'',referencePersonName:'' });
    setError('');
    setRecaptchaToken('');
    recaptchaRef.current?.reset();
  };

  /* ── Page wrapper ─────────────────────────────────────────────────────── */
  const page = {
    minHeight: '100vh',
    background: '#f5f5f5',
    fontFamily: "'Segoe UI', Arial, sans-serif",
    display: 'flex',
    flexDirection: 'column',
  };

  if (submitted) {
    return (
      <div style={page}>
        <SiteHeader isMobile={isMobile} />
        <div style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '48px 16px' }}>
          <div style={{ background: '#fff', borderRadius: 12, boxShadow: '0 4px 24px rgba(0,0,0,0.10)', maxWidth: 520, width: '100%', padding: isMobile ? '36px 24px' : '48px 40px', textAlign: 'center' }}>
            <div style={{ width: 72, height: 72, borderRadius: '50%', background: '#22c55e', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 36, color: '#fff', margin: '0 auto 20px' }}>✓</div>
            <h2 style={{ fontSize: 26, color: '#1a1a1a', margin: '0 0 12px', fontWeight: 700 }}>Thank You!</h2>
            <p style={{ fontSize: 15, color: '#555', lineHeight: 1.7, margin: '0 0 28px' }}>
              Your enquiry has been submitted successfully.<br />Our team will contact you shortly.
            </p>
            <button onClick={resetForm} style={{ background: '#e05b1a', color: '#fff', border: 'none', borderRadius: 8, padding: '12px 32px', fontSize: 15, fontWeight: 700, cursor: 'pointer', letterSpacing: 0.3 }}>
              Submit Another Enquiry
            </button>
          </div>
        </div>
        <SiteFooter isMobile={isMobile} />
      </div>
    );
  }

  return (
    <div style={page}>
      <SiteHeader isMobile={isMobile} />

      {/* Form section */}
      <div style={{ flex: 1, display: 'flex', justifyContent: 'center', padding: isMobile ? '20px 12px' : '36px 16px' }}>
        <div style={{ background: '#fff', borderRadius: 12, boxShadow: '0 4px 24px rgba(0,0,0,0.09)', width: '100%', maxWidth: 820, overflow: 'hidden' }}>

          {/* Form title bar */}
          <div style={{ background: '#1c2b4a', padding: isMobile ? '16px 20px' : '18px 36px' }}>
            <h2 style={{ margin: 0, color: '#fff', fontSize: isMobile ? 16 : 19, fontWeight: 700, letterSpacing: 0.5 }}>
              Student Enquiry Form
            </h2>
            <p style={{ margin: '4px 0 0', color: 'rgba(255,255,255,0.65)', fontSize: 12 }}>
              Fill in your details below — we'll get back to you within 24 hours.
            </p>
          </div>

          {/* Orange accent line */}
          <div style={{ height: 3, background: 'linear-gradient(90deg,#e05b1a,#f5a623)' }} />

          {/* Watermark */}
          <div style={{ position: 'relative' }}>
            <div style={{ position: 'absolute', top: '50%', left: '50%', transform: 'translate(-50%,-50%)', pointerEvents: 'none', userSelect: 'none', zIndex: 0 }}>
              <img src="/logo.png" alt="" style={{ width: isMobile ? 200 : 300, opacity: 0.05, filter: 'grayscale(100%)' }} />
            </div>

            <form onSubmit={handleSubmit} style={{ padding: isMobile ? '20px 20px 28px' : '28px 36px 36px', position: 'relative', zIndex: 1 }}>

              {/* Name + Phone */}
              <Row isMobile={isMobile}>
                <Field label="Full Name" required half isMobile={isMobile}>
                  <input style={inp('name')} {...fb('name')} placeholder="Enter your full name" value={form.name} onChange={set('name')} required />
                </Field>
                <Field label="Contact Number" required half isMobile={isMobile}>
                  <input style={inp('phone')} {...fb('phone')} type="tel" placeholder="10-digit mobile number" value={form.phone} onChange={set('phone')} maxLength={15} required />
                </Field>
              </Row>

              {/* Email + Qualification */}
              <Row isMobile={isMobile}>
                <Field label="Email ID" half isMobile={isMobile}>
                  <input style={inp('email')} {...fb('email')} type="email" placeholder="your@email.com" value={form.email} onChange={set('email')} />
                </Field>
                <Field label="Qualification" required half isMobile={isMobile}>
                  <select style={inp('qualification')} {...fb('qualification')} value={form.qualification} onChange={set('qualification')} required>
                    <option value="">Select qualification</option>
                    {QUALIFICATION_OPTIONS.map(q => <option key={q} value={q}>{q}</option>)}
                  </select>
                </Field>
              </Row>

              {/* Year + Mode */}
              <Row isMobile={isMobile}>
                <Field label="Year of Passed Out" required half isMobile={isMobile}>
                  <select style={inp('yearOfPassedOut')} {...fb('yearOfPassedOut')} value={form.yearOfPassedOut} onChange={set('yearOfPassedOut')} required>
                    <option value="">Select year</option>
                    {YEAR_OPTIONS.map(y => <option key={y} value={y}>{y}</option>)}
                  </select>
                </Field>
                <Field label="Preferred Mode" required half isMobile={isMobile}>
                  <div style={{ display: 'flex', gap: 28, padding: '11px 13px', border: '1.5px solid #d1d5db', borderRadius: 7, background: '#fafafa' }}>
                    {['offline', 'online'].map(m => (
                      <label key={m} style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: isMobile ? 15 : 14, color: '#374151', cursor: 'pointer' }}>
                        <input type="radio" name="preferredMode" value={m} checked={form.preferredMode === m} onChange={set('preferredMode')} style={{ accentColor: '#e05b1a', width: 17, height: 17 }} />
                        {m.charAt(0).toUpperCase() + m.slice(1)}
                      </label>
                    ))}
                  </div>
                </Field>
              </Row>

              {/* Course + Batch */}
              <Row isMobile={isMobile}>
                <Field label="Course Interested" required half isMobile={isMobile}>
                  <select style={inp('courseId')} {...fb('courseId')} value={form.courseId} onChange={set('courseId')} required>
                    <option value="">{courses.length === 0 ? 'Loading courses…' : 'Select a course'}</option>
                    {courses.map(c => <option key={c._id} value={c._id}>{c.name}</option>)}
                  </select>
                </Field>
                <Field label="Batch" half isMobile={isMobile}>
                  <select
                    style={{ ...inp('batchId'), opacity: (!form.courseId || batches.length === 0) ? 0.55 : 1 }}
                    {...fb('batchId')}
                    value={form.batchId}
                    onChange={set('batchId')}
                    disabled={!form.courseId || batches.length === 0}
                  >
                    <option value="">{!form.courseId ? 'Select a course first' : batches.length === 0 ? 'No batches available' : 'Select a batch'}</option>
                    {batches.map(b => <option key={b._id} value={b._id}>{b.name} — {b.mode === 'online' ? 'Online' : 'Offline'}</option>)}
                  </select>
                </Field>
              </Row>

              {/* Batch timing banner */}
              {form.batchTimings && (
                <div style={{ display: 'flex', alignItems: 'center', gap: 12, background: '#fff7ed', border: '1.5px solid #fed7aa', borderRadius: 8, padding: '10px 14px', marginBottom: isMobile ? 12 : 16 }}>
                  <span style={{ fontSize: 20, flexShrink: 0 }}>🕐</span>
                  <div>
                    <p style={{ margin: 0, fontSize: 11, fontWeight: 700, color: '#c2410c', textTransform: 'uppercase', letterSpacing: 0.5 }}>Batch Timings</p>
                    <p style={{ margin: '2px 0 0', fontSize: 14, color: '#7c2d12', fontWeight: 500 }}>{form.batchTimings}</p>
                  </div>
                </div>
              )}

              {/* Source + Reference */}
              <Row isMobile={isMobile}>
                <Field label="How did you hear about us?" required half isMobile={isMobile}>
                  <select style={inp('source')} {...fb('source')} value={form.source} onChange={set('source')} required>
                    <option value="">Select source</option>
                    {SOURCE_OPTIONS.map(s => <option key={s.value} value={s.value}>{s.label}</option>)}
                  </select>
                </Field>
                <Field label="Reference Person Name" half isMobile={isMobile}>
                  <input style={inp('referencePersonName')} {...fb('referencePersonName')} placeholder="Name of person who referred you" value={form.referencePersonName} onChange={set('referencePersonName')} />
                </Field>
              </Row>

              {/* ── Honeypot (hidden from humans, bots fill it) ───────────── */}
              <input
                type="text"
                name="_hp"
                tabIndex={-1}
                autoComplete="off"
                aria-hidden="true"
                style={{ position: 'absolute', left: '-9999px', width: 1, height: 1, opacity: 0, pointerEvents: 'none' }}
              />

              {/* ── Google reCAPTCHA v2 ──────────────────────────────────── */}
              <div style={{ marginBottom: isMobile ? 14 : 18, display: 'flex', flexDirection: 'column', alignItems: 'flex-start', gap: 6 }}>
                <ReCAPTCHA
                  ref={recaptchaRef}
                  sitekey={process.env.REACT_APP_RECAPTCHA_SITE_KEY || '6LeIxAcTAAAAAJcZVRqyHh71UMIEGNQ_MXjiZKhI'}
                  onChange={token => setRecaptchaToken(token || '')}
                  onExpired={() => setRecaptchaToken('')}
                  onErrored={() => setRecaptchaToken('')}
                />
              </div>

              {error && (
                <div style={{ color: '#dc2626', fontSize: 13, background: '#fef2f2', border: '1px solid #fecaca', borderRadius: 8, padding: '10px 14px', marginBottom: 14 }}>
                  {error}
                </div>
              )}

              <button
                type="submit"
                disabled={loading}
                style={{ width: '100%', background: loading ? '#f0a070' : '#e05b1a', color: '#fff', border: 'none', borderRadius: 8, padding: isMobile ? '14px' : '13px', fontSize: isMobile ? 16 : 15, fontWeight: 700, cursor: loading ? 'not-allowed' : 'pointer', letterSpacing: 0.4, marginTop: 4 }}
              >
                {loading ? 'Submitting…' : 'Submit Enquiry'}
              </button>
            </form>
          </div>
        </div>
      </div>

      <SiteFooter isMobile={isMobile} />
    </div>
  );
}

/* ══════════════════════════════════════════════════════════════════════════
   SITE HEADER  (matches emaxtech.com)
══════════════════════════════════════════════════════════════════════════ */
function SiteHeader({ isMobile }) {
  const NAV_LINKS = [
    { label: 'HOME',        href: 'https://emaxtech.com/' },
    { label: 'COURSES',     href: 'https://emaxtech.com/courses/' },
    { label: 'PLACEMENTS',  href: 'https://emaxtech.com/placements/' },
    { label: 'CORPORATE',   href: 'https://emaxtech.com/corporate/' },
    { label: 'BLOG',        href: 'https://emaxtech.com/blog/' },
    { label: 'CONTACT US',  href: 'https://emaxtech.com/contact-us/' },
    { label: 'DEVCON',      href: 'https://emaxtech.com/evcon/sap.html' },
  ];

  return (
    <header>
      {/* ── Top contact bar — scrolls away naturally ─────────────────────── */}
      {!isMobile && (
        <div style={{ background: '#1c2b4a', padding: '7px 32px', display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 8 }}>
          {/* Social icons */}
          <div style={{ display: 'flex', gap: 10, alignItems: 'center' }}>
            {[
              { href: 'https://www.facebook.com/EmaxTechnologiesOfficial/',                        label: 'f',  bg: '#1877f2' },
              { href: 'https://www.linkedin.com/company/emaxtechnologies/?_l=en_US',               label: 'in', bg: '#0077b5' },
              { href: 'https://www.youtube.com/channel/UCwnPE3624nJO7Vdvo0hLUDA',                  label: '▶',  bg: '#ff0000' },
              { href: 'https://www.instagram.com/emax_it/',                                        label: '◈',  bg: '#e1306c' },
              { href: 'https://x.com/Emax_Hyd',                                                    label: '𝕏',  bg: '#000' },
            ].map(s => (
              <a key={s.label} href={s.href} target="_blank" rel="noreferrer"
                style={{ width: 24, height: 24, borderRadius: '50%', background: s.bg, color: '#fff', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 11, fontWeight: 700, textDecoration: 'none', flexShrink: 0 }}>
                {s.label}
              </a>
            ))}
          </div>
          {/* Contact info */}
          <div style={{ display: 'flex', alignItems: 'center', gap: 20, flexWrap: 'wrap' }}>
            <span style={{ color: 'rgba(255,255,255,0.85)', fontSize: 12, display: 'flex', alignItems: 'center', gap: 6 }}>
              <span style={{ color: '#e05b1a' }}>📞</span>
              +91 99484 44808, 72070 66727, 73826 43569
            </span>
            <span style={{ color: 'rgba(255,255,255,0.85)', fontSize: 12, display: 'flex', alignItems: 'center', gap: 6 }}>
              <span style={{ color: '#e05b1a' }}>✉</span>
              info@emaxtech.com
            </span>
          </div>
        </div>
      )}

      {/* ── Logo + Nav: sticky — stays at top when blue bar scrolls away ── */}
      <div style={{ position: 'sticky', top: 0, zIndex: 1000, boxShadow: '0 2px 8px rgba(0,0,0,0.10)' }}>

      {/* Logo bar */}
      <div style={{ background: '#fff', padding: isMobile ? '12px 16px' : '10px 32px', display: 'flex', alignItems: 'center', justifyContent: 'space-between', borderBottom: '1px solid #eee' }}>
        <a href="https://emaxtech.com/" target="_blank" rel="noreferrer" style={{ textDecoration: 'none' }}>
          <img src="/logo.png" alt="emax Technologies" style={{ height: isMobile ? 48 : 64, width: 'auto', objectFit: 'contain' }} />
        </a>
        {isMobile && (
          <a href="tel:+919948444808" style={{ background: '#e05b1a', color: '#fff', borderRadius: 6, padding: '7px 14px', fontSize: 12, fontWeight: 700, textDecoration: 'none' }}>
            Call Us
          </a>
        )}
      </div>

      {/* Navigation bar */}
      <nav style={{ background: '#fff', borderBottom: '2px solid #e05b1a', overflowX: 'auto' }}>
        <div style={{ display: 'flex', alignItems: 'center', padding: isMobile ? '0 8px' : '0 32px', gap: 0, whiteSpace: 'nowrap' }}>
          {NAV_LINKS.map(link => (
            <a
              key={link.label}
              href={link.href}
              target="_blank"
              rel="noreferrer"
              style={{ padding: isMobile ? '10px 10px' : '13px 16px', fontSize: isMobile ? 11 : 13, fontWeight: 600, color: '#1c2b4a', textDecoration: 'none', letterSpacing: 0.3, borderBottom: '2px solid transparent', display: 'inline-block', transition: 'color 0.2s, border-color 0.2s' }}
              onMouseEnter={e => { e.target.style.color = '#e05b1a'; e.target.style.borderBottomColor = '#e05b1a'; }}
              onMouseLeave={e => { e.target.style.color = '#1c2b4a'; e.target.style.borderBottomColor = 'transparent'; }}
            >
              {link.label}
            </a>
          ))}
        </div>
      </nav>
      </div>{/* end sticky wrapper */}
    </header>
  );
}

/* ══════════════════════════════════════════════════════════════════════════
   SITE FOOTER  (matches emaxtech.com)
══════════════════════════════════════════════════════════════════════════ */
function SiteFooter({ isMobile }) {
  const COL_STYLE = { flex: '1 1 180px', minWidth: 140 };
  const HEADING   = { color: '#fff', fontSize: 13, fontWeight: 700, textTransform: 'uppercase', letterSpacing: 1, marginBottom: 14, paddingBottom: 8, borderBottom: '1px solid rgba(255,255,255,0.12)' };
  const LINK_STYLE = { color: 'rgba(255,255,255,0.65)', fontSize: 13, textDecoration: 'none', display: 'block', marginBottom: 7, lineHeight: 1.4, transition: 'color 0.2s' };

  return (
    <footer>
      {/* ── Main footer body ────────────────────────────────────────────── */}
      <div style={{ background: '#1a1a2e', padding: isMobile ? '32px 20px' : '48px 48px 36px' }}>
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 36, maxWidth: 1100, margin: '0 auto' }}>

          {/* Col 1: Logo + About */}
          <div style={{ ...COL_STYLE, flex: '1 1 220px' }}>
            <img src="/logo.png" alt="emax Technologies"
              style={{ height: 56, objectFit: 'contain', background: '#fff', borderRadius: 6, padding: '4px 8px', marginBottom: 14 }} />
            <p style={{ color: 'rgba(255,255,255,0.60)', fontSize: 13, lineHeight: 1.7, margin: 0 }}>
              Emax Technologies is a leading ERP training institute based in Hyderabad, renowned for delivering high-quality classroom and online training programs.
            </p>
          </div>

          {/* Col 2: Courses */}
          <div style={COL_STYLE}>
            <p style={HEADING}>Courses</p>
            {[
              { label: 'SAP ABAP Training',    href: 'https://emaxtech.com/courses/sap-abap-training/' },
              { label: 'SAP FICO Training',    href: 'https://emaxtech.com/courses/sap-fico-training/' },
              { label: 'SAP MM Training',      href: 'https://emaxtech.com/courses/sap-mm-training/' },
              { label: 'SAP PP Training',      href: 'https://emaxtech.com/courses/sap-pp-training/' },
              { label: 'SAP PM Training',      href: 'https://emaxtech.com/courses/sap-pm-training/' },
              { label: 'SAP PI/PO Training',   href: 'https://emaxtech.com/courses/sap-pipo-training/' },
              { label: 'SAP UI5/FIORI',        href: 'https://emaxtech.com/courses/sap-ui5-fiori-training/' },
              { label: 'SAP HANA Admin',       href: 'https://emaxtech.com/courses/sap-hana-admin-training/' },
              { label: 'SAP BW/HANA Training', href: 'https://emaxtech.com/courses/sap-bw-hana-training/' },
              { label: 'Data Science',         href: 'https://emaxtech.com/courses/data-science-training/' },
            ].map(c => (
              <a key={c.label} href={c.href} target="_blank" rel="noreferrer" style={LINK_STYLE}
                onMouseEnter={e => e.target.style.color='#e05b1a'} onMouseLeave={e => e.target.style.color='rgba(255,255,255,0.65)'}>
                {c.label}
              </a>
            ))}
          </div>

          {/* Col 3: Master Courses */}
          <div style={COL_STYLE}>
            <p style={HEADING}>Master Courses</p>
            {[
              { label: 'S/4HANA Finance',     href: 'https://emaxtech.com/sap-s4hana-finance/' },
              { label: 'S/4HANA Logistics',   href: 'https://emaxtech.com/sap-s-4-hana-logistics/' },
              { label: 'HANA Development',    href: 'https://emaxtech.com/sap-hana-development/' },
            ].map(c => (
              <a key={c.label} href={c.href} target="_blank" rel="noreferrer" style={LINK_STYLE}
                onMouseEnter={e => e.target.style.color='#e05b1a'} onMouseLeave={e => e.target.style.color='rgba(255,255,255,0.65)'}>
                {c.label}
              </a>
            ))}
          </div>

          {/* Col 4: Contact Info */}
          <div style={{ ...COL_STYLE, flex: '1 1 220px' }}>
            <p style={HEADING}>Contact Info</p>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
              <div style={{ display: 'flex', gap: 10, alignItems: 'flex-start' }}>
                <span style={{ color: '#e05b1a', fontSize: 14, marginTop: 1, flexShrink: 0 }}>📞</span>
                <span style={{ color: 'rgba(255,255,255,0.7)', fontSize: 13, lineHeight: 1.6 }}>
                  +91 99484 44808<br />72070 66727
                </span>
              </div>
              <div style={{ display: 'flex', gap: 10, alignItems: 'flex-start' }}>
                <span style={{ color: '#e05b1a', fontSize: 14, marginTop: 1, flexShrink: 0 }}>✉</span>
                <a href="mailto:info@emaxtech.com" style={{ color: 'rgba(255,255,255,0.7)', fontSize: 13, textDecoration: 'none' }}>
                  info@emaxtech.com
                </a>
              </div>
              <div style={{ display: 'flex', gap: 10, alignItems: 'flex-start' }}>
                <span style={{ color: '#e05b1a', fontSize: 14, marginTop: 2, flexShrink: 0 }}>📍</span>
                <span style={{ color: 'rgba(255,255,255,0.7)', fontSize: 13, lineHeight: 1.6 }}>
                  Behind HUDA, Shantinilaya Aprt,<br />
                  1st Floor, Gayatri Nagar,<br />
                  Ameerpet, Hyderabad — 500 038.
                </span>
              </div>
            </div>

            {/* Follow Us */}
            <p style={{ ...HEADING, marginTop: 20 }}>Follow Us</p>
            <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }}>
              {[
                { href: 'https://www.facebook.com/EmaxTechnologiesOfficial/',                 label: 'f',  bg: '#1877f2' },
                { href: 'https://www.linkedin.com/company/emaxtechnologies/?_l=en_US',        label: 'in', bg: '#0077b5' },
                { href: 'https://www.youtube.com/channel/UCwnPE3624nJO7Vdvo0hLUDA',           label: '▶',  bg: '#ff0000' },
                { href: 'https://www.instagram.com/emax_it/',                                 label: '◈',  bg: '#e1306c' },
                { href: 'https://x.com/Emax_Hyd',                                             label: '𝕏',  bg: '#000' },
              ].map(s => (
                <a key={s.label} href={s.href} target="_blank" rel="noreferrer"
                  style={{ width: 34, height: 34, borderRadius: '50%', background: s.bg, color: '#fff', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 13, fontWeight: 700, textDecoration: 'none' }}>
                  {s.label}
                </a>
              ))}
            </div>
          </div>
        </div>
      </div>

      {/* ── Acknowledgement strip ───────────────────────────────────────── */}
      <div style={{ background: '#13132a', padding: isMobile ? '20px 20px' : '24px 48px' }}>
        <p style={{ color: 'rgba(255,255,255,0.45)', fontSize: 11.5, lineHeight: 1.7, textAlign: 'center', maxWidth: 900, margin: '0 auto 16px', padding: '0 8px' }}>
          SAP® is a registered trademark of SAP SE in Germany and in several other countries. All SAP modules are proprietary products of SAP SE.
          This website provides educational resources related to SAP® modules. Emax Technologies is in no way associated, sponsored, or endorsed by SAP SE.
        </p>
        {/* Copyright */}
        <div style={{ textAlign: 'center', borderTop: '1px solid rgba(255,255,255,0.1)', paddingTop: 14 }}>
          <p style={{ color: 'rgba(255,255,255,0.50)', fontSize: 12, margin: 0 }}>
            © 2001–2026 · Emax Technologies. All Rights Reserved.
          </p>
        </div>
      </div>
    </footer>
  );
}
